import React, { useState, useMemo } from 'react';
import {
  Box,
  Typography,
  IconButton,
  Popover,
  FormControlLabel,
  Checkbox,
  Button,
  Tooltip,
} from '@mui/material';
import { ViewColumn as ViewColumnIcon } from '@mui/icons-material';
import {
  HistoryModal as HistoryModalDialog,
  HistoryGrid,
  QuickFilter,
} from '@1823-partners/core';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import { fetchEntryHistory } from '../api';
import creFields from '../data/creFields.json';

dayjs.extend(utc);

// Build a lookup of field key -> display name from creFields.json
const FIELD_NAMES = Object.fromEntries(
  Object.entries(creFields).map(([key, cfg]) => [key, cfg.name || key])
);

// Fields excluded from the history grid entirely (UI-only action column)
const EXCLUDED_FIELDS = new Set(['EDIT']);
// Fields pinned left, always visible, not in column chooser
const PINNED_FIELDS = new Set(['CUSIP', 'LOAN_NAME']);
// Audit fields: handled automatically by HistoryGrid (MODIFIED_BY, MODIFIED_DATE)
const AUDIT_FIELDS = new Set(['MODIFIED_BY', 'MODIFIED_DATE']);

// Category display order for the column chooser
const CATEGORY_ORDER = ['Loan', 'Property', 'ClearWater', 'AM', 'Accounting', 'Allocation', 'ESG'];

// Fields visible by default (matching the original hardcoded set)
const DEFAULT_VISIBLE = new Set([
  'ENTITY', 'LOAN_CATEGORY', 'LOAN_TYPE', 'INTEREST_RATE', 'SPREAD',
  'CURRENT_BALANCE', 'ORIGINAL_BALANCE', 'MARKET_VALUE', 'PARTICIPATION',
  'PROPERTY_NAME', 'PROPERTY_TYPE', 'CITY', 'STATE_ID',
  'RISK_RATING_CURRENT', 'DSCR', 'CUR_BAL_LTV',
  'MATURITY', 'ORIGINATION_DATE', 'STATUS',
]);

// Build column groups dynamically from creFields.json, grouped by category
const COLUMN_GROUPS = CATEGORY_ORDER
  .map((category) => ({
    label: category,
    fields: Object.entries(creFields)
      .filter(
        ([key, cfg]) =>
          cfg.category === category &&
          !EXCLUDED_FIELDS.has(key) &&
          !PINNED_FIELDS.has(key) &&
          !AUDIT_FIELDS.has(key)
      )
      .map(([key, cfg]) => ({ field: key, headerName: cfg.name || key })),
  }))
  .filter((group) => group.fields.length > 0);

const ALL_TOGGLEABLE_FIELDS = COLUMN_GROUPS.flatMap((g) =>
  g.fields.map((f) => f.field)
);

// Initial hidden set: everything not in DEFAULT_VISIBLE
const INITIAL_HIDDEN = new Set(
  ALL_TOGGLEABLE_FIELDS.filter((f) => !DEFAULT_VISIBLE.has(f))
);

// Build a value formatter based on field metadata
const getValueFormatter = (cfg) => {
  if (cfg.datatype === 'numeric') {
    if (cfg.subtype === 'percent') {
      return (p) =>
        p.value !== null && p.value !== undefined && p.value !== ''
          ? (parseFloat(p.value) * 100).toFixed(2) + '%'
          : '';
    }
    if (cfg.subtype === 'pct') {
      return (p) =>
        p.value !== null && p.value !== undefined && p.value !== ''
          ? parseFloat(p.value).toFixed(2) + '%'
          : '';
    }
    if (cfg.subtype === 'bps') {
      return (p) =>
        p.value !== null && p.value !== undefined && p.value !== ''
          ? parseFloat(p.value).toFixed(0) + ' bps'
          : '';
    }
    if (cfg.subtype === 'int') {
      return (p) =>
        p.value != null
          ? Number(p.value).toLocaleString('en-US', { maximumFractionDigits: 0 })
          : '';
    }
    if (cfg.subtype === 'no-commas') {
      return (p) => (p.value != null ? String(p.value) : '');
    }
    return (p) =>
      p.value != null
        ? Number(p.value).toLocaleString('en-US', { maximumFractionDigits: 2 })
        : '';
  }
  if (cfg.datatype === 'date') {
    // Date-only fields arrive as 'YYYY-MM-DD'. Render in a UTC frame so the
    // calendar date does not shift for clients west of UTC.
    return (p) => {
      if (!p.value) return '';
      const d = dayjs.utc(p.value);
      return d.isValid() ? d.format('M/D/YYYY') : p.value;
    };
  }
  if (cfg.datatype === 'datetime') {
    return (p) => {
      if (!p.value) return '';
      try { return new Date(p.value).toLocaleString(); } catch { return p.value; }
    };
  }
  if (cfg.datatype === 'boolean') {
    return (p) => {
      if (p.value === true || p.value === 'true') return 'Yes';
      if (p.value === false || p.value === 'false') return 'No';
      return p.value != null ? String(p.value) : '';
    };
  }
  return undefined;
};

// Build all column definitions (pinned + toggleable data fields)
// No version column, no MODIFIED_BY/MODIFIED_DATE — HistoryGrid adds those automatically.
const ALL_COLUMNS = (() => {
  const cols = [];

  // 1. Pinned identifying fields (always visible)
  PINNED_FIELDS.forEach((key) => {
    const cfg = creFields[key];
    if (!cfg) return;
    cols.push({
      headerName: cfg.name || key,
      field: key,
      pinned: 'left',
    });
  });

  // 2. All toggleable data fields, in category order
  CATEGORY_ORDER.forEach((category) => {
    Object.entries(creFields).forEach(([key, cfg]) => {
      if (
        cfg.category === category &&
        !EXCLUDED_FIELDS.has(key) &&
        !PINNED_FIELDS.has(key) &&
        !AUDIT_FIELDS.has(key)
      ) {
        const def = {
          headerName: cfg.name || key,
          field: key,
        };
        if (cfg.datatype === 'numeric') {
          def.type = 'numericColumn';
        }
        const formatter = getValueFormatter(cfg);
        if (formatter) {
          def.valueFormatter = formatter;
        }
        cols.push(def);
      }
    });
  });

  return cols;
})();

const HistoryModal = ({ open, onClose, loanData }) => {
  const [hiddenColumns, setHiddenColumns] = useState(INITIAL_HIDDEN);
  const [columnMenuAnchor, setColumnMenuAnchor] = useState(null);

  const cusip = loanData?.CUSIP;
  const title = loanData?.LOAN_NAME || cusip || '';

  // --- Column visibility ---

  const toggleColumn = (field) => {
    setHiddenColumns((prev) => {
      const next = new Set(prev);
      if (next.has(field)) next.delete(field);
      else next.add(field);
      return next;
    });
  };

  const toggleCategory = (group) => {
    setHiddenColumns((prev) => {
      const next = new Set(prev);
      const allHidden = group.fields.every((f) => next.has(f.field));
      group.fields.forEach((f) => {
        if (allHidden) next.delete(f.field);
        else next.add(f.field);
      });
      return next;
    });
  };

  const showAllColumns = () => setHiddenColumns(new Set());
  const hideAllColumns = () => setHiddenColumns(new Set(ALL_TOGGLEABLE_FIELDS));

  // Filter out hidden columns for HistoryGrid
  const visibleColumns = useMemo(
    () => ALL_COLUMNS.filter((c) => c.pinned || !hiddenColumns.has(c.field)),
    [hiddenColumns]
  );

  const visibleCount = ALL_TOGGLEABLE_FIELDS.length - hiddenColumns.size;

  return (
    <HistoryModalDialog
      open={open}
      onClose={onClose}
      title={`Entry History — ${title}`}
    >
      <HistoryGrid
        fetchHistory={() => fetchEntryHistory(cusip)}
        fetchParams={[open, cusip]}
        columns={visibleColumns}
        fieldNames={FIELD_NAMES}
        defaultColDef={{ minWidth: 130, tooltipShowDelay: 500 }}
        renderToolbar={({ quickFilterText, onQuickFilterChange }) => (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5 }}>
            <QuickFilter
              value={quickFilterText}
              onChange={onQuickFilterChange}
              placeholder="Quick filter entry history..."
              sx={{ flex: 1 }}
            />
            <Tooltip title="Choose columns">
              <IconButton
                size="small"
                onClick={(e) => setColumnMenuAnchor(e.currentTarget)}
                sx={{
                  border: 1,
                  borderColor: hiddenColumns.size > 0 ? 'primary.main' : 'divider',
                  borderRadius: 1,
                  color: hiddenColumns.size > 0 ? 'primary.main' : 'action.active',
                }}
              >
                <ViewColumnIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          </Box>
        )}
      />

      {/* Column chooser popover */}
      <Popover
        open={Boolean(columnMenuAnchor)}
        anchorEl={columnMenuAnchor}
        onClose={() => setColumnMenuAnchor(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        <Box sx={{ p: 2, minWidth: 260, maxHeight: 500, overflow: 'auto' }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
            <Typography variant="subtitle2">
              Columns ({visibleCount}/{ALL_TOGGLEABLE_FIELDS.length})
            </Typography>
            <Box sx={{ display: 'flex', gap: 0.5 }}>
              <Button size="small" onClick={showAllColumns}>All</Button>
              <Button size="small" onClick={hideAllColumns}>None</Button>
            </Box>
          </Box>
          {COLUMN_GROUPS.map((group) => {
            const groupVisibleCount = group.fields.filter((f) => !hiddenColumns.has(f.field)).length;
            return (
              <Box key={group.label} sx={{ mb: 1.5 }}>
                <Box
                  sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', cursor: 'pointer' }}
                  onClick={() => toggleCategory(group)}
                >
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    sx={{ fontWeight: 600, textTransform: 'uppercase', letterSpacing: 0.5 }}
                  >
                    {group.label}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {groupVisibleCount}/{group.fields.length}
                  </Typography>
                </Box>
                {group.fields.map(({ field, headerName }) => (
                  <FormControlLabel
                    key={field}
                    sx={{ display: 'flex', ml: 0, mr: 0 }}
                    control={
                      <Checkbox
                        size="small"
                        checked={!hiddenColumns.has(field)}
                        onChange={() => toggleColumn(field)}
                      />
                    }
                    label={<Typography variant="body2">{headerName}</Typography>}
                  />
                ))}
              </Box>
            );
          })}
        </Box>
      </Popover>
    </HistoryModalDialog>
  );
};

export default HistoryModal;
